// Collision: skater vs goal frame (posts, back, sides of net).
//
// Owns:  the rectangular goal boxes at each end of the rink.
// Reads: tuning.rink (length, goalLineOffset, goalDepth, goalMouthHalfWidth),
//        tuning.player.radius.
// Mutates: player.pos + player.vel.
// Emits:  no events.
//
// Each goal is modelled as a solid axis-aligned box. A skater cannot stand
// inside the box (posts/crossbar-equivalent/back of net). The puck's own
// collision world (see `hockey/puck-motion.ts`) treats the goal mouth as
// OPEN so shots score normally; only the back + sides of the net bounce.
//
// Zero-allocation.

import { tuning } from "../state/tuning.js";
import type { PlayerState } from "../state/types.js";

function pushPlayerOutOfBox(
  player: PlayerState,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  r: number,
): void {
  const px = player.pos.x;
  const py = player.pos.y;
  const cx = px < x1 ? x1 : px > x2 ? x2 : px;
  const cy = py < y1 ? y1 : py > y2 ? y2 : py;

  if (cx !== px || cy !== py) {
    // Center outside the box: standard circle-vs-rectangle overlap test.
    const dx = px - cx;
    const dy = py - cy;
    const distSq = dx * dx + dy * dy;
    if (distSq >= r * r) return;
    const dist = Math.sqrt(distSq);
    if (dist === 0) return;
    const nx = dx / dist;
    const ny = dy / dist;
    const overlap = r - dist;
    player.pos.x += nx * overlap;
    player.pos.y += ny * overlap;
    const intoWall = -(player.vel.x * nx + player.vel.y * ny);
    if (intoWall > 0) {
      player.vel.x += nx * intoWall;
      player.vel.y += ny * intoWall;
    }
    return;
  }

  // Center inside the box: push out via the shortest axial exit.
  const dLeft = px - x1;
  const dRight = x2 - px;
  const dDown = py - y1;
  const dUp = y2 - py;
  let minD = dLeft;
  let normX = -1;
  let normY = 0;
  if (dRight < minD) {
    minD = dRight;
    normX = 1;
    normY = 0;
  }
  if (dDown < minD) {
    minD = dDown;
    normX = 0;
    normY = -1;
  }
  if (dUp < minD) {
    minD = dUp;
    normX = 0;
    normY = 1;
  }
  player.pos.x = px + normX * (minD + r);
  player.pos.y = py + normY * (minD + r);
  const intoWall = -(player.vel.x * normX + player.vel.y * normY);
  if (intoWall > 0) {
    player.vel.x += normX * intoWall;
    player.vel.y += normY * intoWall;
  }
}

/**
 * Treats both goal nets as solid axis-aligned boxes for skaters: a skater
 * cannot stand inside the net or pass through the posts, crossbar-equivalent,
 * or back of the net. The mouth is closed for players (the puck still scores
 * via the goal-line crossing test in `tickGoalDetection`, and a controlled
 * puck enters via spring pull). Zero-allocation.
 */
export function resolvePlayerVsGoals(player: PlayerState): void {
  const halfL = tuning.rink.length / 2;
  const halfMouth = tuning.rink.goalMouthHalfWidth;
  const r = tuning.player.radius;

  const awayLineX = halfL - tuning.rink.goalLineOffset;
  const awayBackX = awayLineX + tuning.rink.goalDepth;
  pushPlayerOutOfBox(player, awayLineX, -halfMouth, awayBackX, halfMouth, r);

  const homeLineX = -halfL + tuning.rink.goalLineOffset;
  const homeBackX = homeLineX - tuning.rink.goalDepth;
  pushPlayerOutOfBox(player, homeBackX, -halfMouth, homeLineX, halfMouth, r);
}
